import { z } from 'zod';
import { isoDaySchema as isoDate } from './dates';
import { ALERT_CRITICALITIES, ALERT_STATUSES, ALERT_TYPES } from './alerts';
import { DIAGNOSIS_STATUSES } from './diagnosis';
import { paginatedSchema, paginationQuerySchema } from './pagination';

/** Origem do plano de ação: o que motivou as ações (PRD §5.1, RN-28; DEC-041). */
export const ACTION_ORIGIN_TYPES = [
  'INITIATIVE',
  'ALERT',
  'DECISION',
  'DIAGNOSIS',
  'PRIORITIZATION',
] as const;
export type ActionOriginTypeName = (typeof ACTION_ORIGIN_TYPES)[number];
export const ACTION_ORIGIN_LABELS: Record<ActionOriginTypeName, string> = {
  INITIATIVE: 'Iniciativa',
  ALERT: 'Alerta / desvio',
  DECISION: 'Decisão de reunião',
  DIAGNOSIS: 'Item de diagnóstico',
  PRIORITIZATION: 'Matriz de priorização',
};
/**
 * Origens que podem ser escolhidas ao criar o plano à mão (a entidade de origem existe). Cada tarefa que cria
 * alertas (T-110), diagnóstico (T-160) e matriz (T-161) acrescenta a sua aqui (DEC-041). A "decisão de reunião"
 * não entra: o plano dela nasce na condução da análise crítica, vinculado à decisão (T-121b; DEC-050).
 */
export const ACTION_ORIGINS_AVAILABLE: readonly ActionOriginTypeName[] = [
  'INITIATIVE',
  'ALERT',
  'DIAGNOSIS',
  'PRIORITIZATION',
];
/** Origens que já existem nos planos (filtro da lista): as escolhidas à mão e as que nascem em outro fluxo. */
export const ACTION_ORIGINS_LINKED: readonly ActionOriginTypeName[] = [
  'INITIATIVE',
  'ALERT',
  'DECISION',
  'DIAGNOSIS',
  'PRIORITIZATION',
];
/** Origens cujas ações exigem responsável e prazo (RN-14; DEC-041): compromissos de execução. */
export const ACTION_ORIGINS_REQUIRING_OWNER_AND_DUE: readonly ActionOriginTypeName[] = [
  'INITIATIVE',
  'ALERT',
  'DECISION',
];

/** Status da ação (PRD §11.19). */
export const ACTION_STATUSES = ['PENDING', 'IN_PROGRESS', 'IN_VALIDATION', 'DONE', 'CANCELLED'] as const;
export type ActionStatusName = (typeof ACTION_STATUSES)[number];
export const ACTION_STATUS_LABELS: Record<ActionStatusName, string> = {
  PENDING: 'A fazer',
  IN_PROGRESS: 'Em andamento',
  IN_VALIDATION: 'Em validação',
  DONE: 'Concluído',
  CANCELLED: 'Cancelado',
};
/** Status encerrados (não contam como atrasados). */
export const ACTION_CLOSED_STATUSES: readonly ActionStatusName[] = ['DONE', 'CANCELLED'];

/** Atrasada: prazo antes de hoje sem estar concluída nem cancelada. */
export const actionLate = (a: { dueDate: string | null; status: ActionStatusName }, today: string): boolean =>
  a.dueDate !== null && a.dueDate < today && !ACTION_CLOSED_STATUSES.includes(a.status);

/** A ação desta origem precisa de responsável e prazo? (cancelada nunca precisa). */
export const actionNeedsOwnerAndDue = (origin: ActionOriginTypeName, status: ActionStatusName): boolean =>
  status !== 'CANCELLED' && ACTION_ORIGINS_REQUIRING_OWNER_AND_DUE.includes(origin);

/** Progresso do plano: concluídas sobre as não canceladas (null sem nenhuma ação valendo). */
export function actionPlanProgress(actions: { status: ActionStatusName }[]): number | null {
  const counted = actions.filter((a) => a.status !== 'CANCELLED');
  if (!counted.length) return null;
  const done = counted.filter((a) => a.status === 'DONE').length;
  return Math.round((done / counted.length) * 10000) / 100;
}

const ref = z.object({ id: z.string().uuid(), name: z.string() });

// ---------------------------------------------------------------------------------------------
// Progresso, checklist e atualizações da ação (T-094a, RF-52, RN-26; DEC-045)

/** De onde vem o progresso: concluída (100%), checklist (marcados/total) ou o percentual informado. */
export const ACTION_PROGRESS_SOURCES = ['STATUS', 'CHECKLIST', 'MANUAL'] as const;
export type ActionProgressSourceName = (typeof ACTION_PROGRESS_SOURCES)[number];

/** Progresso da ação: concluída = 100%; com checklist, marcados sobre o total; senão, o informado (ou 0%). */
export function actionProgress(a: {
  status: ActionStatusName;
  progress: number | null;
  checklist: readonly { done: boolean }[];
}): { value: number; source: ActionProgressSourceName } {
  if (a.status === 'DONE') return { value: 100, source: 'STATUS' };
  if (a.checklist.length) {
    const done = a.checklist.filter((i) => i.done).length;
    return { value: Math.round((done / a.checklist.length) * 100), source: 'CHECKLIST' };
  }
  return { value: a.progress ?? 0, source: 'MANUAL' };
}

export const actionChecklistItemSchema = z.object({
  id: z.string().uuid(),
  text: z.string(),
  done: z.boolean(),
  /** Quem marcou e quando (nulos enquanto desmarcado). */
  doneAt: z.string().nullable(),
  doneByName: z.string().nullable(),
});
export type ActionChecklistItem = z.infer<typeof actionChecklistItemSchema>;

export const ACTION_CHECKLIST_MAX_ITEMS = 50;
export const actionChecklistInputSchema = z.object({
  text: z
    .string()
    .trim()
    .min(1, 'Escreva o item do checklist.')
    .max(300, 'Item longo demais (até 300 caracteres).'),
});
export type ActionChecklistInput = z.input<typeof actionChecklistInputSchema>;
export const actionChecklistCheckSchema = z.object({ done: z.boolean() });
export const actionChecklistOrderSchema = z.object({
  ids: z.array(z.string().uuid()).min(1, 'Informe a ordem dos itens.').max(ACTION_CHECKLIST_MAX_ITEMS),
});

/** Limite da justificativa da atualização (é também o motivo da trilha de auditoria). */
export const ACTION_NOTE_MAX = 1000;
/** Atualização da ação: status, percentual (sem checklist) e justificativa — ao menos um dos três. */
export const actionUpdateInputSchema = z
  .object({
    status: z.enum(ACTION_STATUSES).optional(),
    progress: z
      .preprocess(
        (v) => (typeof v === 'string' && v.trim() !== '' ? Number(v) : v === '' ? null : v),
        z
          .number({ message: 'Informe o percentual em números.' })
          .int('Use um número inteiro.')
          .min(0, 'O percentual vai de 0 a 100.')
          .max(100, 'O percentual vai de 0 a 100.')
          .nullable(),
      )
      .optional(),
    note: z
      .string()
      .trim()
      .max(ACTION_NOTE_MAX, `A justificativa pode ter no máximo ${ACTION_NOTE_MAX} caracteres.`)
      .nullable()
      .optional()
      .transform((v) => v || null),
  })
  .refine(
    (v) => v.status !== undefined || (v.progress !== undefined && v.progress !== null) || v.note !== null,
    {
      message: 'Mude o status, informe o percentual ou escreva a justificativa.',
      path: ['note'],
    },
  );
export type ActionUpdateInput = z.input<typeof actionUpdateInputSchema>;

export const actionUpdateSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(ACTION_STATUSES),
  progress: z.number().int(),
  note: z.string().nullable(),
  createdByName: z.string(),
  createdAt: z.string(),
});
export type ActionUpdate = z.infer<typeof actionUpdateSchema>;
export const actionUpdateListSchema = z.object({ items: z.array(actionUpdateSchema) });

export const planActionSchema = z.object({
  id: z.string().uuid(),
  planId: z.string().uuid(),
  /** What — o quê. */
  title: z.string(),
  /** Why — por quê. */
  why: z.string().nullable(),
  /** Where — onde. */
  location: z.string().nullable(),
  /** When — início e prazo. */
  startDate: z.string().nullable(),
  dueDate: z.string().nullable(),
  /** Who — quem. */
  responsible: ref.nullable(),
  /** How — como. */
  how: z.string().nullable(),
  /** How much — quanto custa (R$). */
  cost: z.number().nullable(),
  status: z.enum(ACTION_STATUSES),
  completedAt: z.string().nullable(),
  late: z.boolean(),
  /** Concluir exige a evidência de execução (T-092; DEC-042). */
  requiresEvidence: z.boolean(),
  /** Evidência de execução: nome do arquivo e link de download (assinado, expira). */
  evidence: z.object({ name: z.string(), url: z.string() }).nullable(),
  /** Progresso (0–100) e de onde vem (T-094a; DEC-045). */
  progress: z.number().int(),
  progressSource: z.enum(ACTION_PROGRESS_SOURCES),
  /** Percentual informado na última atualização (null = nunca informado). */
  reportedProgress: z.number().int().nullable(),
  checklist: z.array(actionChecklistItemSchema),
});
export type PlanAction = z.infer<typeof planActionSchema>;

/** Ação no quadro (kanban): com o plano e a origem dele. */
export const projectActionSchema = planActionSchema.extend({
  plan: ref.extend({
    origin: z.object({
      type: z.enum(ACTION_ORIGIN_TYPES),
      initiative: ref.nullable(),
    }),
  }),
});
export type ProjectAction = z.infer<typeof projectActionSchema>;

/** Até 500 ações por consulta, com o total (o quadro avisa quando há mais e pede filtros — DEC-042). */
export const PROJECT_ACTIONS_LIMIT = 500;
export const projectActionListSchema = z.object({
  items: z.array(projectActionSchema),
  total: z.number().int(),
});
export type ProjectActionList = z.infer<typeof projectActionListSchema>;

/** Filtros do quadro: os de ação da lista de planos, mais plano, iniciativa e origem (planos ativos). */
export const projectActionsQuerySchema = z.object({
  q: z.string().trim().max(100, 'Busca longa demais.').optional(),
  planId: z.string().uuid().optional(),
  initiativeId: z.string().uuid().optional(),
  originType: z.enum(ACTION_ORIGIN_TYPES).optional(),
  responsibleId: z.string().uuid().optional(),
  dueFrom: isoDate.optional(),
  dueTo: isoDate.optional(),
  late: z.enum(['true', 'false']).optional(),
  /** Status (o calendário filtra; no quadro o status é a coluna). */
  status: z.enum(ACTION_STATUSES).optional(),
});
export type ProjectActionsQuery = z.input<typeof projectActionsQuerySchema>;

export const actionPlanSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  origin: z.object({
    type: z.enum(ACTION_ORIGIN_TYPES),
    /** Iniciativa de origem (origem "iniciativa"). */
    initiative: ref.extend({ archived: z.boolean() }).nullable(),
    /**
     * Alerta de origem (origem "alerta/desvio" — RN-13; T-110b). Título e status só para quem enxerga o alerta
     * (módulo Alertas e escopo da área/unidade dele); para os demais vêm nulos (oculto).
     */
    alert: z
      .object({
        id: z.string().uuid(),
        type: z.enum(ALERT_TYPES),
        title: z.string().nullable(),
        status: z.enum(ALERT_STATUSES).nullable(),
      })
      .nullable(),
    /**
     * Decisão de origem (origem "decisão de reunião" — T-121b; DEC-050), com a reunião em que foi tomada. Texto e
     * reunião só para quem enxerga a reunião (módulo Reuniões e área dela ou participante); para os demais, nulos.
     */
    decision: z
      .object({
        id: z.string().uuid(),
        text: z.string().nullable(),
        meeting: z.object({ id: z.string().uuid(), title: z.string(), startsAt: z.string() }).nullable(),
      })
      .nullable(),
    /**
     * Item de diagnóstico de origem (origem "item de diagnóstico" — T-160a; DEC-061). Título e status só para quem
     * enxerga o item (módulo Diagnóstico e escopo da área/unidade dele); para os demais, nulos.
     */
    diagnosis: z
      .object({
        id: z.string().uuid(),
        title: z.string().nullable(),
        status: z.enum(DIAGNOSIS_STATUSES).nullable(),
      })
      .nullable(),
  }),
  owner: ref.nullable(),
  department: ref.nullable(),
  businessUnit: ref.nullable(),
  archivedAt: z.string().nullable(),
  /** Contagem sobre TODAS as ações do plano (mesmo com filtros na lista). */
  summary: z.object({
    total: z.number().int(),
    done: z.number().int(),
    cancelled: z.number().int(),
    late: z.number().int(),
    /** Concluídas sobre as não canceladas, em %. */
    progress: z.number().nullable(),
  }),
  /** Ações do plano (na lista, só as que passam nos filtros de ação). */
  actions: z.array(planActionSchema),
});
export type ActionPlan = z.infer<typeof actionPlanSchema>;
export const actionPlanListSchema = paginatedSchema(actionPlanSchema);

export const actionPlanListQuerySchema = paginationQuerySchema.extend({
  /** Busca no nome do plano e no "o quê" das ações. */
  q: z.string().trim().max(100, 'Busca longa demais.').optional(),
  originType: z.enum(ACTION_ORIGIN_TYPES).optional(),
  initiativeId: z.string().uuid().optional(),
  /** Planos ligados ao objetivo: das iniciativas dele e de alertas e decisões sobre ele ou sobre KPIs dele (DEC-052). */
  objectiveId: z.string().uuid().optional(),
  responsibleId: z.string().uuid().optional(),
  status: z.enum(ACTION_STATUSES).optional(),
  /** Prazo a partir de / até (inclusive). */
  dueFrom: isoDate.optional(),
  dueTo: isoDate.optional(),
  late: z.enum(['true', 'false']).optional(),
  archived: z.enum(['true', 'false']).optional(),
});
export type ActionPlanListQuery = z.input<typeof actionPlanListQuerySchema>;

const optionalText = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, `${label} pode ter no máximo ${max} caracteres.`)
    .nullable()
    .optional()
    .transform((v) => v || null);
const optionalId = (message: string) =>
  z
    .string()
    .uuid(message)
    .nullable()
    .optional()
    .transform((v) => v ?? null);

/** Cadastro do plano. A origem é fixada na criação (o que motivou as ações não muda depois). */
export const actionPlanInputSchema = z
  .object({
    name: z.string().trim().min(3, 'Informe o nome do plano.').max(160, 'Nome longo demais.'),
    description: optionalText(2000, 'A descrição'),
    originType: z.enum(ACTION_ORIGIN_TYPES, { message: 'Escolha a origem do plano.' }),
    initiativeId: optionalId('Iniciativa inválida.'),
    alertId: optionalId('Alerta inválido.'),
    diagnosisItemId: optionalId('Item de diagnóstico inválido.'),
    ownerId: optionalId('Responsável inválido.'),
    departmentId: optionalId('Área inválida.'),
    businessUnitId: optionalId('Unidade inválida.'),
  })
  .refine((v) => v.originType !== 'INITIATIVE' || v.initiativeId !== null, {
    message: 'Escolha a iniciativa que motivou o plano.',
    path: ['initiativeId'],
  })
  .refine((v) => v.originType !== 'ALERT' || v.alertId !== null, {
    message: 'Escolha o alerta que motivou o plano.',
    path: ['alertId'],
  })
  .refine((v) => v.originType !== 'DIAGNOSIS' || v.diagnosisItemId !== null, {
    message: 'Escolha o item de diagnóstico que motivou o plano.',
    path: ['diagnosisItemId'],
  })
  .refine((v) => v.originType !== 'PRIORITIZATION' || v.diagnosisItemId !== null, {
    message: 'Escolha o item da matriz que motivou o plano.',
    path: ['diagnosisItemId'],
  });
export type ActionPlanInput = z.input<typeof actionPlanInputSchema>;

/** Edição do plano (a origem não muda). */
export const actionPlanUpdateSchema = z.object({
  name: z.string().trim().min(3, 'Informe o nome do plano.').max(160, 'Nome longo demais.'),
  description: optionalText(2000, 'A descrição'),
  ownerId: optionalId('Responsável inválido.'),
  departmentId: optionalId('Área inválida.'),
  businessUnitId: optionalId('Unidade inválida.'),
});
export type ActionPlanUpdate = z.input<typeof actionPlanUpdateSchema>;

/** Ação com 5W2H (criar e substituir com PUT — mande a ação inteira). */
export const planActionInputSchema = z
  .object({
    title: z.string().trim().min(3, 'Diga o que será feito.').max(300, 'Texto longo demais.'),
    why: optionalText(2000, 'O porquê'),
    location: optionalText(300, 'O onde'),
    startDate: isoDate
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    dueDate: isoDate
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    responsibleId: optionalId('Responsável inválido.'),
    how: optionalText(2000, 'O como'),
    cost: z.coerce
      .number({ message: 'Informe o custo em números.' })
      .min(0, 'O custo não pode ser negativo.')
      .max(999_999_999_999.99, 'Valor alto demais.')
      .multipleOf(0.01, 'Use no máximo duas casas decimais.')
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    status: z.enum(ACTION_STATUSES).default('PENDING'),
    requiresEvidence: z.boolean().default(false),
  })
  .refine((v) => !v.startDate || !v.dueDate || v.startDate <= v.dueDate, {
    message: 'O prazo precisa ser igual ou posterior ao início.',
    path: ['dueDate'],
  });
export type PlanActionInput = z.input<typeof planActionInputSchema>;

/** Novo prazo da ação (calendário — T-093; DEC-043). */
export const planActionDueSchema = z.object({ dueDate: isoDate });

/** Mudança rápida de status (concluir, cancelar, reabrir). */
export const planActionStatusSchema = z.object({ status: z.enum(ACTION_STATUSES) });

/** Opções do formulário e dos filtros. */
export const actionPlanOptionsSchema = z.object({
  /** Iniciativas ativas no escopo, com a área do objetivo (a área padrão do plano). */
  initiatives: z.array(ref.extend({ departmentId: z.string().uuid().nullable() })),
  people: z.array(ref),
  departments: z.array(ref),
  businessUnits: z.array(ref),
  /**
   * Alertas abertos (aberto ou em tratamento) no escopo, para a origem "alerta" — vazio sem acesso ao módulo
   * Alertas. Com a área/unidade do alerta (a padrão do plano).
   */
  alerts: z.array(
    z.object({
      id: z.string().uuid(),
      title: z.string(),
      type: z.enum(ALERT_TYPES),
      criticality: z.enum(ALERT_CRITICALITIES),
      departmentId: z.string().uuid().nullable(),
      businessUnitId: z.string().uuid().nullable(),
    }),
  ),
  /**
   * Itens de diagnóstico que ainda podem originar um plano (não arquivados) no escopo, para a origem "item de
   * diagnóstico" — vazio sem "editar" no Diagnóstico. Com a área/unidade do item (a padrão do plano).
   */
  diagnosisItems: z.array(
    z.object({
      id: z.string().uuid(),
      title: z.string(),
      departmentId: z.string().uuid(),
      businessUnitId: z.string().uuid().nullable(),
      /** Enviado à matriz de priorização (pode originar plano com a origem "matriz" — DEC-062). */
      inMatrix: z.boolean(),
    }),
  ),
  /** Origens que já podem ser escolhidas ao criar o plano (DEC-041). */
  origins: z.array(z.enum(ACTION_ORIGIN_TYPES)),
  /** Origens do filtro da lista (inclui as que nascem em outro fluxo, como a decisão de reunião). */
  filterOrigins: z.array(z.enum(ACTION_ORIGIN_TYPES)),
});
export type ActionPlanOptions = z.infer<typeof actionPlanOptionsSchema>;
